import {
    ApolloClient,
    InMemoryCache,
    createHttpLink,
    from,
    ApolloLink,
    DefaultOptions,
    TypePolicy,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { ORGANIZATION_STORAGE_KEY } from './constants';
import { logError } from '@utils/error';
import { faultLink, simulateFault, clearFault } from './apollo-fault-link';
import { clearAuthSessionStorage, getAuthToken } from '@auth/session-storage';
import possibleTypesResult from '@generated/possibleTypes.json';
import { isNotProduction } from '@utils/environment';
import { HttpStatusCode } from './http-status-codes';
import { config } from '@config';

// Direct error notification functions
const showErrorToast = (message: string) => {
    window.dispatchEvent(
        new CustomEvent('error-overlay-network', {
            detail: { message },
        })
    );
};

const showInfoToast = (message: string) => {
    // For info messages, we can use the same system but maybe with different styling later
    window.dispatchEvent(
        new CustomEvent('error-overlay-network', {
            detail: { message },
        })
    );
};

const possibleTypes = (possibleTypesResult as { possibleTypes: Record<string, string[]> })
    .possibleTypes;

const GRAPHQL_URL = config.graphUrl;

// Attach operation name (header) and, in dev, append it to the URL for easy network debugging
const operationNameLink = new ApolloLink((operation, forward) => {
    const name = operation.operationName || '';
    operation.setContext((prev: Record<string, any>) => {
        const ctx: typeof prev & { uri?: string } = {
            ...prev,
            headers: {
                ...((prev as any).headers || {}),
                'x-apollo-operation-name': name,
            },
        };
        if (isNotProduction() && name) {
            ctx.uri = `${GRAPHQL_URL}?operationName=${encodeURIComponent(name)}`;
        }
        return ctx;
    });
    return forward(operation);
});

const httpLink = createHttpLink({ uri: GRAPHQL_URL });

const authLink = setContext((_, { headers }) => {
    const authorization = getAuthToken() || '';
    return {
        headers: {
            ...headers,
            authorization,
        },
    };
});

// Retry queries (not mutations) with capped backoff + jitter
const retryLink = new RetryLink({
    delay: (attempt) => Math.min(500 * 2 ** attempt, 8000) + Math.random() * 250,
    attempts: (count, operation, error) => {
        const isQuery = operation.query.definitions.some(
            (d: any) => d.kind === 'OperationDefinition' && d.operation === 'query'
        );
        if (!isQuery) return false;

        const status =
            (error as any)?.statusCode ??
            (error as any)?.response?.status ??
            (error?.networkError as any)?.statusCode ??
            (error?.networkError as any)?.response?.status;

        // don’t retry auth or generic 4xx
        if (
            [HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden].includes(status) ||
            (status && status < 500)
        )
            return false;
        return count < 4;
    },
});

const ONE_SHOT_RELOAD_KEY = 'ErrorReloadedOnce';

// Use error overlay toasts (no debounced banners)

const isServerGqlError = (e: any) =>
    e?.extensions?.code === 'INTERNAL_SERVER_ERROR' ||
    e?.extensions?.code === 'SERVICE_UNAVAILABLE' ||
    e?.extensions?.code === 'GATEWAY_TIMEOUT' ||
    (typeof e?.extensions?.http?.status === 'number' && e.extensions.http.status >= 500);

const errorLink = onError(({ graphQLErrors, networkError, operation, response }) => {
    const route = window.location?.pathname;
    const orgId = sessionStorage.getItem(ORGANIZATION_STORAGE_KEY) || undefined;
    const opName = operation?.operationName;
    const statusCode =
        (networkError as any)?.statusCode ?? (networkError as any)?.response?.status ?? undefined;
    const requestId =
        (response as any)?.headers?.get?.('x-request-id') ??
        (networkError as any)?.response?.headers?.get?.('x-request-id') ??
        undefined;

    if (graphQLErrors?.length) {
        for (const err of graphQLErrors) {
            logError(err, {
                where: 'apollo',
                route,
                orgId,
                requestId,
                operation: opName,
                extra: { code: err.extensions?.code, path: err.path },
            });
        }
    }

    if (networkError) {
        logError(networkError, {
            where: 'apollo',
            route,
            orgId,
            requestId,
            operation: opName,
            extra: { statusCode },
        });
    }

    // Auth-only logout
    const shouldLogout =
        graphQLErrors?.some((e) => e.extensions?.code === 'UNAUTHENTICATED') ||
        (!!networkError &&
            [HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden].includes(Number(statusCode)));

    if (shouldLogout) {
        clearAuthSessionStorage();
        window.location.reload();
        return;
    }

    // Non-auth errors → show as toast notifications via error overlay
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

    if (offline) {
        // Offline: connection-recovery banner handles UX; avoid duplicate toast
        return;
    }

    const hasServerGqlError = !!graphQLErrors?.some(isServerGqlError);

    if (networkError || hasServerGqlError) {
        // Dispatch network/server error to error overlay
        const errorMsg = networkError
            ? `Network error (${statusCode || 'unknown'}): Server temporarily unavailable`
            : "Server error: We're having trouble processing your request";

        window.dispatchEvent(
            new CustomEvent('error-overlay-network', { detail: { message: errorMsg } })
        );
    }

    // One-time hard reload for clear 502/503/504 or opaque TypeError, never while offline
    const isHardTransient =
        [
            HttpStatusCode.BadGateway,
            HttpStatusCode.ServiceUnavailable,
            HttpStatusCode.GatewayTimeout,
        ].includes(Number(statusCode)) ||
        (networkError && (networkError as any).name === 'TypeError');

    const reloadedOnce = sessionStorage.getItem(ONE_SHOT_RELOAD_KEY) === '1';

    if (!offline && isHardTransient && !reloadedOnce) {
        sessionStorage.setItem(ONE_SHOT_RELOAD_KEY, '1');
        setTimeout(() => window.location.reload(), 600);
    }
});

// Clear banner on successful next responses (no GraphQL errors)
const clearOnSuccessLink = new ApolloLink((operation, forward) =>
    forward(operation).map((result) => {
        // Clear banner when errors resolve (no action needed for error overlay)
        if (!result.errors) {
            // Error toasts auto-dismiss, no manual clearing needed
        }
        return result;
    })
);

const defaultOptions: DefaultOptions = {
    watchQuery: { fetchPolicy: 'cache-and-network', errorPolicy: 'all' },
    query: { errorPolicy: 'all' },
    mutate: { errorPolicy: 'none' },
};

export const apolloClient = new ApolloClient({
    link: from([
        // Put retry BEFORE errorLink so errorLink sees only terminal failures
        retryLink,
        errorLink,
        authLink,
        operationNameLink,
        ...(isNotProduction() ? [faultLink] : []),
        clearOnSuccessLink,
        httpLink,
    ]),
    cache: new InMemoryCache({
        possibleTypes: { ...possibleTypes },
        typePolicies: {
            ChargeSystem: mergeInvoicingInfoSinceTheyLackId(),
        },
    }),
    defaultOptions,
});

function mergeInvoicingInfoSinceTheyLackId(): TypePolicy {
    return {
        fields: {
            invoicingInfo: {
                merge(existing, incoming, { mergeObjects }) {
                    return mergeObjects(existing, incoming);
                },
            },
        },
    };
}

// HMR: auto-refetch when back online (prevent duplicate listeners)
const ONLINE_HANDLER_KEY = '__apollo_online_handler__';
if (!(window as any)[ONLINE_HANDLER_KEY]) {
    (window as any)[ONLINE_HANDLER_KEY] = true;
    window.addEventListener('online', () => {
        // Use banner for persistent status updates
        window.dispatchEvent(
            new CustomEvent('apollo:banner', {
                detail: {
                    level: 'info',
                    description: 'Back online. Syncing data…',
                },
            })
        );
        apolloClient.reFetchObservableQueries().finally(() => {
            // Clear the banner after sync completes
            window.dispatchEvent(new CustomEvent('apollo:banner-clear'));
        });
    });
}

// handy dev console hooks
if (isNotProduction()) {
    (window as any).__APOLLO_SIM__ = {
        simulateFault, // (status?: number, times?: number)
        clearFault,
        showErrorToast,
        showInfoToast,
        // Banner system for persistent info/warning messages (errors go to toasts)
        showInfoBanner: (desc: string) =>
            window.dispatchEvent(
                new CustomEvent('apollo:banner', { detail: { level: 'info', description: desc } })
            ),
        showWarningBanner: (desc: string) =>
            window.dispatchEvent(
                new CustomEvent('apollo:banner', {
                    detail: { level: 'warning', description: desc },
                })
            ),
        clearBanner: () => window.dispatchEvent(new CustomEvent('apollo:banner-clear')),
        client: apolloClient,
    };
}
